import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { workloadService, type WorkloadAllocation, type WorkloadCapacity } from '../../../services/projectManagement/workloadService';
import { pmMembersService, buildMemberMap, getDisplayName, type ProjectManagementMember } from '../../../services/projectManagement/membersService';
import { projectsService, type Project } from '../../../services/projectManagement/projectsService';

const WorkloadTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();

  const [loading, setLoading] = useState(true);
  const [capacities, setCapacities] = useState<WorkloadCapacity[]>([]);
  const [allocations, setAllocations] = useState<WorkloadAllocation[]>([]);
  const [members, setMembers] = useState<ProjectManagementMember[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [capacityUserId, setCapacityUserId] = useState('');
  const [capacityHours, setCapacityHours] = useState<number>(40);

  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocUserId, setAllocUserId] = useState('');
  const [allocProjectId, setAllocProjectId] = useState<string>('');
  const [allocStart, setAllocStart] = useState('');
  const [allocEnd, setAllocEnd] = useState('');
  const [allocPercent, setAllocPercent] = useState<number>(0);
  const [allocHours, setAllocHours] = useState<number>(0);

  const memberMap = useMemo(() => buildMemberMap(members), [members]);
  const projectMap = useMemo(() => {
    return (projects || []).reduce((acc, p) => {
      acc[String(p.id)] = p;
      return acc;
    }, {} as Record<string, Project>);
  }, [projects]);

  const fetchAll = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const [mRes, pRes, cRes, aRes] = await Promise.all([
        pmMembersService.list(activeOrgId),
        projectsService.list({ organization_id: activeOrgId }),
        workloadService.listCapacities({ organization_id: activeOrgId }),
        workloadService.listAllocations({ organization_id: activeOrgId }),
      ]);

      setMembers(mRes.data || []);
      setProjects(pRes.data || []);

      if (cRes.error) {
        toast.error(String(cRes.error || 'Failed to load capacities'));
        setCapacities([]);
      } else {
        setCapacities(cRes.data || []);
      }

      if (aRes.error) {
        toast.error(String(aRes.error || 'Failed to load allocations'));
        setAllocations([]);
      } else {
        setAllocations(aRes.data || []);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load workload');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const openCapacity = (userId?: string) => {
    setCapacityUserId(userId || '');
    const existing = capacities.find((c) => String(c.user_id) === String(userId));
    setCapacityHours(existing ? Number(existing.weekly_capacity_hours || 40) : 40);
    setShowCapacityModal(true);
  };

  const saveCapacity = async () => {
    if (!activeOrgId) return;
    if (!capacityUserId) {
      toast.error('Select a member');
      return;
    }
    try {
      const { data, error } = await workloadService.upsertCapacity({
        organization_id: activeOrgId,
        user_id: capacityUserId,
        weekly_capacity_hours: Number(capacityHours || 0),
      });
      if (error) {
        toast.error(String(error || 'Failed to save capacity'));
        return;
      }
      setShowCapacityModal(false);
      setCapacityUserId('');
      setCapacityHours(40);
      if (data) {
        void fetchAll();
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save capacity');
    }
  };

  const openAllocation = () => {
    setAllocUserId('');
    setAllocProjectId('');
    setAllocStart('');
    setAllocEnd('');
    setAllocPercent(0);
    setAllocHours(0);
    setShowAllocationModal(true);
  };

  const createAllocation = async () => {
    if (!activeOrgId) return;
    if (!allocUserId) {
      toast.error('Select a member');
      return;
    }
    if (!allocStart || !allocEnd) {
      toast.error('Start and end dates are required');
      return;
    }

    try {
      const { data, error } = await workloadService.createAllocation({
        organization_id: activeOrgId,
        user_id: allocUserId,
        project_id: allocProjectId ? allocProjectId : null,
        start_date: allocStart,
        end_date: allocEnd,
        allocation_percent: Number(allocPercent || 0),
        allocation_hours: Number(allocHours || 0),
      });

      if (error) {
        toast.error(String(error || 'Failed to create allocation'));
        return;
      }

      setShowAllocationModal(false);
      if (data) {
        void fetchAll();
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create allocation');
    }
  };

  const deleteAllocation = async (id: string) => {
    if (!activeOrgId) return;
    try {
      const { error } = await workloadService.deleteAllocation(id, activeOrgId);
      if (error) {
        toast.error(String(error || 'Failed to delete allocation'));
        return;
      }
      void fetchAll();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete allocation');
    }
  };

  if (!activeOrgId) {
    return <div className="rounded-xl bg-white p-6">Select an organization to view workload.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-gray-900">Workload</div>
            <div className="text-sm text-gray-600">Capacity planning with allocation over time</div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => openCapacity()}
              className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Set capacity
            </button>
            <button
              onClick={openAllocation}
              className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-primary/90"
            >
              Add allocation
            </button>
          </div>
        </div>

        {loading ? (
          <div className="mt-4 text-sm text-gray-500">Loading...</div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-200">
              <div className="border-b border-gray-200 px-4 py-3">
                <div className="text-sm font-semibold text-gray-900">Capacities</div>
              </div>
              <div className="divide-y">
                {capacities.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-gray-500">No capacities set yet.</div>
                ) : (
                  capacities.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{getDisplayName(memberMap[String(c.user_id)])}</div>
                        <div className="text-xs text-gray-500">{String(c.user_id)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-gray-900">{Number(c.weekly_capacity_hours || 0)}h/wk</div>
                        <button
                          onClick={() => openCapacity(String(c.user_id))}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200">
              <div className="border-b border-gray-200 px-4 py-3">
                <div className="text-sm font-semibold text-gray-900">Allocations</div>
              </div>
              <div className="divide-y">
                {allocations.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-gray-500">No allocations yet.</div>
                ) : (
                  allocations.map((a) => (
                    <div key={a.id} className="flex items-start justify-between gap-3 px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{getDisplayName(memberMap[String(a.user_id)])}</div>
                        <div className="text-xs text-gray-500">{a.start_date} → {a.end_date}</div>
                        <div className="text-xs text-gray-500">
                          {a.project_id ? (projectMap[String(a.project_id)]?.name || String(a.project_id)) : 'Unassigned'}
                          {' · '}
                          {Number(a.allocation_percent || 0)}% / {Number(a.allocation_hours || 0)}h
                        </div>
                      </div>
                      <button
                        onClick={() => deleteAllocation(String(a.id))}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showCapacityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">Set capacity</div>
              <button onClick={() => setShowCapacityModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700">Member</label>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={capacityUserId}
                  onChange={(e) => setCapacityUserId(e.target.value)}
                >
                  <option value="">Select</option>
                  {members.map((m) => (
                    <option key={m.user_id} value={m.user_id}>{getDisplayName(m)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700">Weekly capacity (hours)</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  type="number"
                  min={0}
                  value={capacityHours}
                  onChange={(e) => setCapacityHours(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setShowCapacityModal(false)}
                className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveCapacity}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showAllocationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">Add allocation</div>
              <button onClick={() => setShowAllocationModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700">Member</label>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={allocUserId}
                  onChange={(e) => setAllocUserId(e.target.value)}
                >
                  <option value="">Select</option>
                  {members.map((m) => (
                    <option key={m.user_id} value={m.user_id}>{getDisplayName(m)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700">Project (optional)</label>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={allocProjectId}
                  onChange={(e) => setAllocProjectId(e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Start date</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    type="date"
                    value={allocStart}
                    onChange={(e) => setAllocStart(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700">End date</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    type="date"
                    value={allocEnd}
                    onChange={(e) => setAllocEnd(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Allocation %</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    type="number"
                    min={0}
                    max={100}
                    value={allocPercent}
                    onChange={(e) => setAllocPercent(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Allocation hours</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    type="number"
                    min={0}
                    value={allocHours}
                    onChange={(e) => setAllocHours(Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setShowAllocationModal(false)}
                className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createAllocation}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkloadTab;
